import { SLOT_MAX_RANK, type ArchetypeId } from '../model/types.ts';

/** Tags from GDD §3.5. Gear and passives key off these names. */
export const SYNERGY_TAGS = [
  'Thread',
  'Bleed',
  'Poison',
  'Shield',
  'Summon',
  'Time',
  'Shadow',
  'Beast',
  'Banner',
  'Curse',
  'Armor-break',
] as const;

export type SynergyTag = (typeof SYNERGY_TAGS)[number];

export type SpecRole = 'tank' | 'damage' | 'support' | 'control';

/** §3.1 signature resources. The Heart Mark owns the pool. */
export const SIGNATURE_RESOURCE: Record<ArchetypeId, string> = {
  ironbound: 'Resolve',
  veilweaver: 'Thread',
  wildkin: 'Verdance',
  shadecaller: 'Debt',
};

/** §3.2 rank caps. The server spend path enforces these same numbers. */
export const DESIGN_SLOT_MAX_RANK = SLOT_MAX_RANK;

/** §3.3 keystone nodes. */
export const KEYSTONE_RANKS = [10, 20, 30] as const;

export interface PassiveNode {
  name: string;
  /** Rank in this sub-spec that unlocks the node. */
  rank: number;
  keystone: boolean;
  text: string;
}

export interface WhisperPassive {
  name: string;
  /** Works only in the Whisper Mark (tertiary) slot. */
  text: string;
}

export interface FusionPassive {
  pair: [ArchetypeId, ArchetypeId];
  /** Chosen by the Heart Mark sub-spec. Three variants per pair. */
  variant: 0 | 1 | 2;
  name: string;
  text: string;
}

/**
 * §3.5: six archetype pairs, three variants each.
 * The Heart Mark's fusion variant picks the line.
 * Frayed Edge, Living Bastion, and Borrowed Time have tick effects.
 * The other lines are selected and shown. Their text does not invent extra combat numbers.
 */
export const FUSION_PASSIVES: FusionPassive[] = [
  { pair: ['ironbound', 'veilweaver'], variant: 0, name: 'Tempered Thread', text: 'Resolve spent also lays a Thread on the target.' },
  { pair: ['ironbound', 'veilweaver'], variant: 1, name: 'Woven Plate', text: 'A Thread on self adds a small shield.' },
  { pair: ['ironbound', 'veilweaver'], variant: 2, name: 'Red Line Loom', text: 'Marked-target damage spreads along one Thread.' },
  { pair: ['ironbound', 'wildkin'], variant: 0, name: 'Living Bastion', text: 'Blocked damage grows a healing grove.' },
  { pair: ['ironbound', 'wildkin'], variant: 1, name: 'Thorn Furnace', text: 'Reflected heat leaves thorny ground.' },
  { pair: ['ironbound', 'wildkin'], variant: 2, name: 'Banner Grove', text: 'A war banner also ticks a small heal.' },
  { pair: ['ironbound', 'shadecaller'], variant: 0, name: 'Frayed Edge', text: 'Sunder strips one extra armor stack.' },
  { pair: ['ironbound', 'shadecaller'], variant: 1, name: 'Oath Debt', text: 'Damage taken while an oath holds is stored as Debt.' },
  { pair: ['ironbound', 'shadecaller'], variant: 2, name: 'Banner Shade', text: 'A war banner also applies a short curse.' },
  { pair: ['veilweaver', 'wildkin'], variant: 0, name: 'Seasonal Thread', text: 'Threads carry the current season stance.' },
  { pair: ['veilweaver', 'wildkin'], variant: 1, name: 'Rooted Hour', text: 'A rewind also advances a healing grove.' },
  { pair: ['veilweaver', 'wildkin'], variant: 2, name: 'Spore Fold', text: 'A fold drags spore stacks with it.' },
  { pair: ['shadecaller', 'veilweaver'], variant: 0, name: 'Borrowed Time', text: 'Rewound HP is recorded as Debt.' },
  { pair: ['shadecaller', 'veilweaver'], variant: 1, name: 'Dark Thread', text: 'Threads count as Shadow for curse spread.' },
  { pair: ['shadecaller', 'veilweaver'], variant: 2, name: 'Decoy Debt', text: 'A decoy that pops adds Debt.' },
  { pair: ['shadecaller', 'wildkin'], variant: 0, name: 'Grave Grove', text: 'A fallen grove raises a short-lived echo.' },
  { pair: ['shadecaller', 'wildkin'], variant: 1, name: 'Spore Debt', text: 'Poison that bursts is called in as Debt.' },
  { pair: ['shadecaller', 'wildkin'], variant: 2, name: 'Hive Echo', text: 'A bee swarm repeats its last sting once.' },
];

function orderedPair(left: ArchetypeId, right: ArchetypeId): [ArchetypeId, ArchetypeId] {
  return left < right ? [left, right] : [right, left];
}

export function fusionFor(left: ArchetypeId, right: ArchetypeId, variant: 0 | 1 | 2): FusionPassive | null {
  if (left === right) return null;
  const [a, b] = orderedPair(left, right);
  return (
    FUSION_PASSIVES.find((row) => {
      const [x, y] = orderedPair(row.pair[0], row.pair[1]);
      return row.variant === variant && x === a && y === b;
    }) ?? null
  );
}
